/* Self-contained module (spec.md §3.0 pattern). Native <video controls> are
   intentionally left off the markup — they're added/removed on hover so the
   edge-to-edge video reads as pure footage until the visitor shows intent to
   interact with it. Touch has no hover, so a tap reveals controls instead of
   also trying to toggle play (avoids fighting the browser's own tap-to-play). */
(function () {
  var video = document.querySelector(".pr-stop-motion__preview");
  if (!video) {
    return;
  }

  // Always start from the first frame on a fresh load or refresh — including
  // when the browser restores the page from its back/forward cache, which
  // would otherwise resume mid-video.
  function rewind() {
    try {
      video.currentTime = 0;
    } catch (err) {
      console.warn("pr-stop-motion: rewind failed", err);
    }
  }
  rewind();
  window.addEventListener("pageshow", function (e) {
    if (e.persisted) {
      rewind();
    }
  });

  function showControls() {
    video.controls = true;
  }

  function hideControls() {
    video.controls = false;
  }

  video.addEventListener("pointerenter", function (e) {
    if (e.pointerType === "touch") {
      return;
    }
    showControls();
  });
  video.addEventListener("pointerleave", function (e) {
    if (e.pointerType === "touch") {
      return;
    }
    hideControls();
  });
  video.addEventListener("focus", showControls);
  video.addEventListener("blur", hideControls);

  video.addEventListener(
    "touchstart",
    function () {
      if (!video.controls) {
        showControls();
      }
    },
    { passive: true }
  );

  // Merchant wants this playing WITH sound by default, but every major browser
  // blocks unmuted autoplay outright — there is no HTML/JS way around that on
  // first load. Best available approximation: try to play unmuted immediately;
  // if the browser rejects it, fall back to muted autoplay (which is allowed)
  // and flip muted off the instant the visitor first interacts with the page
  // at all (not just the video), so sound kicks in as early as physically
  // possible without requiring them to find/click an unmute control.
  function attemptPlay() {
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(function () {
        if (!video.muted) {
          video.muted = true;
          video.play().catch(function () {});
        }
      });
    }
  }

  // pointerdown/keydown/touchstart are genuine "sticky user activation"
  // gestures browsers honor for unmuted media — setting muted = false inside
  // one of these handlers is reliable (unlike the scripted attemptPlay()
  // above, which runs with no gesture behind it and gets rejected). Kept as
  // persistent (non-"once") listeners and re-asserted on every qualifying
  // interaction rather than a single latch, so it self-heals if attemptPlay()
  // ever forces muted back to true after this has already fired once.
  //
  // Deliberately ONLY unmutes — does not call play(). This fires on every
  // pointerdown/keydown/touchstart anywhere on the whole page, not just on
  // the video, so a stray video.play() here was resuming the video the
  // instant it got paused for ANY reason: clicking the native pause button
  // (pointerdown fires as part of that same click, sees video.paused and
  // immediately restarted it — the reported "can't pause" bug) and starting
  // a scroll on touch, since scrolling begins with its own touchstart (the
  // reported "resumes when scrolled out of view" bug). Unmuting a video
  // that's correctly paused is harmless — it just stays silent and paused.
  function unmuteOnInteraction() {
    video.muted = false;
  }
  ["pointerdown", "keydown", "touchstart"].forEach(function (evt) {
    document.addEventListener(evt, unmuteOnInteraction, { passive: true });
  });

  // manuallyPaused tracks an explicit user pause so the offscreen-visibility
  // observer and the tab-visibility handler below don't resume playback the
  // visitor deliberately stopped. Driven off the video's own native play/pause
  // events (not a custom click handler) so it's correct no matter how the
  // pause happened — clicking the native control bar's own pause button,
  // hitting space while it's focused, etc. An earlier version toggled
  // play/pause from a "click" listener on the video itself, which fought
  // with the native control bar: clicking pause there still bubbles a click
  // event up to the video element, so the old handler saw video.paused ==
  // true and immediately called play() again, undoing the pause the visitor
  // just made (reported bug — pause never actually stuck).
  //
  // The 'pause' event also fires for OUR OWN automatic pause() calls (scrolled
  // offscreen, tab backgrounded) — those must not set manuallyPaused, or the
  // video would never be allowed to auto-resume again afterwards. autoPausing
  // flags a pause as ours; the event fires as a queued task (not synchronously)
  // so the flag is cleared inside the handler itself, not right after the call.
  var manuallyPaused = false;
  var autoPausing = false;
  var inView = false;

  // Only flag a pause as ours when it will actually fire a 'pause' event. The
  // old version set autoPausing even when the video was already paused (e.g.
  // the visitor paused it, then scrolled away or switched tabs) — no event
  // fired, the flag stayed stuck at true, and the visitor's NEXT real pause got
  // misread as automatic, so returning to the tab resumed it with sound.
  function autoPause() {
    if (video.paused) {
      return;
    }
    autoPausing = true;
    video.pause();
  }

  function resumeIfAllowed() {
    if (inView && !manuallyPaused && !document.hidden) {
      attemptPlay();
    }
  }

  video.addEventListener("pause", function () {
    if (autoPausing) {
      autoPausing = false;
    } else {
      manuallyPaused = true;
    }
  });
  video.addEventListener("play", function () {
    manuallyPaused = false;
  });

  var reduceMotion =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reduceMotion) {
    autoPause();
    manuallyPaused = true;
  } else if ("IntersectionObserver" in window) {
    // Pause decoding while scrolled out of view; resume on re-entry unless
    // the visitor explicitly paused it themselves.
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          inView = entry.isIntersecting;
          if (inView) {
            resumeIfAllowed();
          } else {
            autoPause();
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(video);
  }

  // Reported bug: switching browser tabs/apps left the video (and its now-
  // unmuted audio) running invisibly in the background — IntersectionObserver
  // only tracks scroll position within the page, not whether the browser tab
  // itself is the visible/foreground one, so it never caught this case.
  // Coming back to the tab only resumes if the video is actually on screen and
  // the visitor hadn't paused it — previously it resumed unconditionally, so
  // sound started wherever on the page the visitor happened to be.
  document.addEventListener("visibilitychange", function () {
    if (document.hidden) {
      autoPause();
    } else {
      resumeIfAllowed();
    }
  });
})();
